import React, { useEffect, useRef, useState } from 'react';
import { format, isValid } from 'date-fns';
import { Button, IconButton, Tooltip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CallMadeIcon from '@material-ui/icons/CallMade';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import RoomOutlinedIcon from '@material-ui/icons/RoomOutlined';

import { InfoTooltip } from 'lib';
import { useDateSettingsFormat } from 'common/useDateSettingsFormat';
import { useTimeSettingsFormat } from 'common/useTimeSettingsFormat';
import { copyToClipboard } from 'helpers/copyToClipboard';
import { coordinatesToPlain, formatCoordinates, googleMapsUrl } from 'helpers/formatCoordinates';
import { styles } from './styles';

const useStyles = makeStyles(styles);

const COPIED_DURATION = 2000;

const GeoLocation = ({ coordinates, showActions }) => {
  const classes = useStyles();
  const getTimeFormatHook = useTimeSettingsFormat();
  const formatForTimePars = getTimeFormatHook().formatForTimePars;
  const { dateFormat } = useDateSettingsFormat();
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef();

  useEffect(() => () => clearTimeout(copiedTimer.current), []);

  const copy = async () => {
    if (!(await copyToClipboard(coordinatesToPlain(coordinates)))) return;
    setCopied(true);
    clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(() => setCopied(false), COPIED_DURATION);
  };

  const copyLabel = copied ? 'Copied' : 'Copy coordinates';
  const capturedAt = new Date(coordinates.capturedAt);

  const content = (
    <div className={classes.content}>
      <Typography variant="body2" className={classes.greyText}>
        Coordinates:
        <div className={classes.coordinates}>
          <span className={classes.value}>{formatCoordinates(coordinates)}</span>
          {showActions && (
            /* Controlled `open`: the tooltip is a post-copy confirmation, not a hover hint. */
            <Tooltip title={copyLabel} open={copied} placement="top">
              <IconButton
                size="small"
                className={classes.copy}
                aria-label={copyLabel}
                onClick={copy}
              >
                <FileCopyOutlinedIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </Typography>
      {isValid(capturedAt) && (
        <Typography variant="body2" className={classes.value}>
          Recorded on {format(capturedAt, dateFormat)} at {format(capturedAt, formatForTimePars)}
        </Typography>
      )}
      {showActions && (
        <Button
          className={classes.mapsLink}
          href={googleMapsUrl(coordinates)}
          target="_blank"
          rel="noopener noreferrer"
          endIcon={<CallMadeIcon className={classes.mapsIcon} />}
        >
          Open in Google Maps
        </Button>
      )}
    </div>
  );

  return (
    <InfoTooltip
      content={content}
      placement="bottom"
      surface="light"
      size="wide"
      interactive={showActions}
      icon={<RoomOutlinedIcon className={classes.pin} fontSize="small" />}
    />
  );
};

export default GeoLocation;
